import { FormEvent, useState } from 'react';
import { NEWSLETTER_HONEYPOT_FIELD, subscribeToNewsletter } from '../../services/newsletter';

interface NewsletterSignupProps {
  tone?: 'onPrimary' | 'default';
}

export default function NewsletterSignup({ tone = 'onPrimary' }: NewsletterSignupProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const onPrimary = tone === 'onPrimary';
  const labelClass = onPrimary ? 'block text-sm text-blue-100 mb-1' : 'block text-sm text-gray-700 mb-1';
  const inputClass = onPrimary
    ? 'w-full px-4 py-3 rounded-lg border-0 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-white/50'
    : 'w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary';

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setResult(null);
    try {
      const next = await subscribeToNewsletter({ email, firstName, lastName, honeypot });
      setResult(next);
      if (next.ok) {
        setEmail('');
        setFirstName('');
        setLastName('');
      }
    } catch (err) {
      setResult({
        ok: false,
        message: err instanceof Error ? err.message : 'Subscription failed.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="relative text-left">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label htmlFor="newsletter-first-name" className={labelClass}>
            First name
          </label>
          <input
            id="newsletter-first-name"
            name="FNAME"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            placeholder="First name"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="newsletter-last-name" className={labelClass}>
            Last name
          </label>
          <input
            id="newsletter-last-name"
            name="LNAME"
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            placeholder="Last name"
            className={inputClass}
          />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <label htmlFor="newsletter-email" className={labelClass}>
            Email
          </label>
          <input
            id="newsletter-email"
            name="EMAIL"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Email address"
            className={inputClass}
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className={`sm:self-end min-h-[48px] px-6 py-3 font-semibold rounded-lg transition-colors disabled:opacity-60 ${
            onPrimary
              ? 'bg-white text-[#00467f] hover:bg-gray-100'
              : 'bg-primary text-white hover:bg-primary-dark'
          }`}
        >
          {submitting ? 'Subscribing…' : 'Subscribe'}
        </button>
      </div>
      <input
        type="text"
        name={NEWSLETTER_HONEYPOT_FIELD}
        value={honeypot}
        onChange={(event) => setHoneypot(event.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
      />
      {result && (
        <p
          role="status"
          className={`text-sm mt-3 ${onPrimary ? 'text-white' : result.ok ? 'text-success' : 'text-error'}`}
        >
          {result.message}
        </p>
      )}
    </form>
  );
}
